import React, { useState, useEffect } from 'react';
import { IconOps, IconQuant, IconRisk, IconClose, IconActivity } from './Icons';
import { analyzePreDealCheck } from '../services/geminiService';
import toast from 'react-hot-toast';

interface TradeTicketProps {
    isOpen: boolean;
    onClose: () => void;
    onBook: (trade: any) => void;
    initialData?: any; // New prop for AI pre-filling
}

const TradeTicket: React.FC<TradeTicketProps> = ({ isOpen, onClose, onBook, initialData }) => {
    const [side, setSide] = useState<'Buy' | 'Sell'>('Buy');
    const [assetClass, setAssetClass] = useState('Equity');
    const [symbol, setSymbol] = useState('AAPL');
    const [quantity, setQuantity] = useState(100);
    const [price, setPrice] = useState(180.00);
    const [counterparty, setCounterparty] = useState('J.P. Morgan');
    
    // Simulated Live Feed State
    const [livePrice, setLivePrice] = useState(180.00);
    const [priceChange, setPriceChange] = useState(0.00);
    
    const [isChecking, setIsChecking] = useState(false);
    const [checkResult, setCheckResult] = useState<{ status: 'Safe' | 'Warning' | 'Critical', message: string } | null>(null);

    const notional = quantity * price;

    // Effect: Handle Pre-Fill Data (e.g. from AI Risk Mitigation)
    useEffect(() => {
        if (initialData) {
            if (initialData.side) setSide(initialData.side);
            if (initialData.assetClass) setAssetClass(initialData.assetClass);
            if (initialData.symbol) setSymbol(initialData.symbol);
            if (initialData.quantity) setQuantity(initialData.quantity);
            if (initialData.price) setPrice(initialData.price);
            if (initialData.counterparty) setCounterparty(initialData.counterparty);
            
            toast("AiQ: Hedging Strategy Auto-Filled", { icon: '🤖' });
        }
    }, [initialData, isOpen]);

    // Simulate Live Market Feed update when symbol changes
    useEffect(() => {
        // In a real app, this would call your Python backend or WebSocket
        const basePrice = initialData?.price || Math.random() * 100 + 100; 
        setLivePrice(Number(basePrice.toFixed(2)));
        if (!initialData) setPrice(Number(basePrice.toFixed(2))); 
        setPriceChange(0);
    }, [symbol, initialData]);

    // Simulate Ticker movement
    useEffect(() => {
        const interval = setInterval(() => {
            const move = (Math.random() - 0.5) * 0.50;
            setLivePrice(prev => {
                const next = Number((prev + move).toFixed(2));
                setPriceChange(next - prev);
                return next;
            });
        }, 3000);
        return () => clearInterval(interval);
    }, []);

    const handleCheck = async () => {
        setIsChecking(true);
        setCheckResult(null);
        
        const result = await analyzePreDealCheck({
            product: `${assetClass} - ${symbol}`,
            side,
            quantity,
            price, 
            counterparty
        }, {
            marketPrice: livePrice 
        });
        
        setCheckResult(result);
        setIsChecking(false);
    };

    const handleBook = () => {
        if (!checkResult) {
            toast.error("Please run Risk Check first.");
            return;
        }
        if (checkResult.status === 'Critical') {
            toast.error("Blocked: Critical Risk Breach.");
            return;
        }
        
        const newTrade = {
            id: `TRD-LIVE-${Math.floor(Math.random() * 1000)}`,
            product: `${symbol} ${assetClass}`,
            counterparty,
            notional,
            currency: 'USD',
            valuation: notional, 
            status: 'Pending',
            tradeDate: new Date().toISOString().split('T')[0],
            settlementDate: new Date(Date.now() + 172800000).toISOString().split('T')[0], // T+2
            trader: 'YOU'
        };
        
        onBook(newTrade);
        onClose();
        toast.success("Trade Booked Successfully");
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm" onClick={onClose}></div>
            
            <div className="relative bg-white w-full max-w-lg rounded-xl shadow-2xl overflow-hidden border border-slate-700 animate-scale-up">
                {/* Header */}
                <div className={`p-4 flex justify-between items-center text-white transition-colors ${initialData ? 'bg-purple-900' : 'bg-slate-900'}`}>
                    <div className="flex items-center gap-2">
                        <div className="bg-white/20 p-1.5 rounded text-white"><IconOps /></div>
                        <div>
                            <h3 className="font-bold">Deal Capture</h3>
                            {initialData && <span className="text-[10px] uppercase bg-purple-500 px-1.5 rounded text-white">AiQ Auto-Fill</span>}
                        </div>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-white"><IconClose /></button>
                </div>

                <div className="p-6 space-y-6">
                    {/* Live Market Context Bar */}
                    <div className="bg-slate-50 border border-slate-200 rounded p-2 flex items-center justify-between text-xs">
                        <div className="flex items-center gap-2">
                            <span className="text-slate-500 font-bold uppercase">Live Feed</span>
                            <span className="animate-pulse w-2 h-2 bg-emerald-500 rounded-full"></span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="font-bold text-slate-700">{symbol}</span>
                            <span className="font-mono text-lg font-bold">{livePrice.toFixed(2)}</span>
                            <span className={priceChange >= 0 ? 'text-emerald-500' : 'text-red-500'}>
                                {priceChange >= 0 ? '▲' : '▼'} {Math.abs(priceChange).toFixed(2)}
                            </span>
                        </div>
                    </div>

                    {/* Buy/Sell Toggle */}
                    <div className="flex bg-slate-100 p-1 rounded-lg">
                        <button 
                            onClick={() => setSide('Buy')}
                            className={`flex-1 py-2 font-bold text-sm rounded-md transition-all ${side === 'Buy' ? 'bg-emerald-500 text-white shadow' : 'text-slate-500 hover:bg-white'}`}
                        >
                            BUY
                        </button>
                        <button 
                            onClick={() => setSide('Sell')}
                            className={`flex-1 py-2 font-bold text-sm rounded-md transition-all ${side === 'Sell' ? 'bg-red-500 text-white shadow' : 'text-slate-500 hover:bg-white'}`}
                        >
                            SELL
                        </button>
                    </div>

                    {/* Fields */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-bold text-slate-500 mb-1">Asset Class</label>
                            <select value={assetClass} onChange={e => setAssetClass(e.target.value)} className="w-full border p-2 rounded text-sm bg-slate-50 outline-none focus:border-blue-500">
                                <option>Equity</option>
                                <option>FX Spot</option>
                                <option>Rates Swap</option>
                                <option>Credit (CDS)</option>
                                <option>Crypto</option>
                            </select>
                        </div>
                        <div>
                             <label className="block text-xs font-bold text-slate-500 mb-1">Ticker / Pair</label>
                             <input type="text" value={symbol} onChange={e => setSymbol(e.target.value)} className="w-full border p-2 rounded text-sm outline-none focus:border-blue-500" />
                        </div>
                        <div>
                             <label className="block text-xs font-bold text-slate-500 mb-1">Quantity</label>
                             <input type="number" value={quantity} onChange={e => setQuantity(Number(e.target.value))} className="w-full border p-2 rounded text-sm outline-none focus:border-blue-500" />
                        </div>
                        <div>
                             <label className="block text-xs font-bold text-slate-500 mb-1">Price</label>
                             <input type="number" value={price} onChange={e => setPrice(Number(e.target.value))} className="w-full border p-2 rounded text-sm outline-none focus:border-blue-500" />
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-slate-500 mb-1">Counterparty</label>
                        <select value={counterparty} onChange={e => setCounterparty(e.target.value)} className="w-full border p-2 rounded text-sm bg-slate-50 outline-none focus:border-blue-500">
                            <option>J.P. Morgan</option>
                            <option>Goldman Sachs</option>
                            <option>Morgan Stanley</option>
                            <option>Citibank</option>
                            <option>Deutsche Bank</option>
                        </select>
                    </div>

                    <div className="flex justify-between items-center p-3 bg-slate-50 rounded border border-slate-200">
                        <span className="text-xs text-slate-500 font-bold uppercase">Total Notional</span>
                        <span className="text-lg font-mono font-bold text-slate-800">${notional.toLocaleString()}</span>
                    </div>

                    {/* AI Risk Check Area */}
                    <div className={`rounded-lg border p-4 transition-all ${
                        !checkResult ? 'bg-slate-50 border-slate-200' :
                        checkResult.status === 'Safe' ? 'bg-emerald-50 border-emerald-200' :
                        checkResult.status === 'Warning' ? 'bg-amber-50 border-amber-200' :
                        'bg-red-50 border-red-200'
                    }`}>
                        <div className="flex items-center justify-between mb-2">
                             <div className="flex items-center gap-2">
                                 <IconRisk />
                                 <span className="text-xs font-bold uppercase text-slate-600">AiQ Risk Check</span>
                             </div>
                             {isChecking && <span className="text-xs text-blue-500 animate-pulse font-medium">Analyzing Limit Usage...</span>}
                        </div>
                        
                        {!checkResult ? (
                            <p className="text-xs text-slate-400 italic">Run risk check before booking.</p>
                        ) : (
                            <div>
                                <div className={`font-bold text-sm ${
                                    checkResult.status === 'Safe' ? 'text-emerald-700' : 
                                    checkResult.status === 'Warning' ? 'text-amber-700' : 'text-red-700'
                                }`}>
                                    Status: {checkResult.status.toUpperCase()}
                                </div>
                                <p className="text-xs text-slate-600 mt-1">{checkResult.message}</p>
                            </div>
                        )}
                    </div>

                    <div className="flex gap-3 pt-2">
                        <button 
                            onClick={handleCheck}
                            className="flex-1 py-3 bg-slate-200 hover:bg-slate-300 text-slate-700 font-bold rounded-lg transition-colors flex items-center justify-center gap-2"
                        >
                            <IconQuant /> Check Risk
                        </button>
                        <button 
                            onClick={handleBook}
                            disabled={!checkResult || checkResult.status === 'Critical'}
                            className={`flex-1 py-3 font-bold rounded-lg text-white shadow-lg transition-all flex items-center justify-center gap-2 ${
                                !checkResult ? 'bg-slate-400 cursor-not-allowed' :
                                checkResult.status === 'Critical' ? 'bg-red-300 cursor-not-allowed' :
                                'bg-blue-600 hover:bg-blue-700 hover:-translate-y-0.5'
                            }`}
                        >
                            Confirm Booking
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TradeTicket;